'use client';

import { useState, useRef, useCallback, useEffect } from 'react';

function isOverflowSet(overflowValue) {
    return (overflowValue === 'auto' ||
        overflowValue === 'hidden' ||
        overflowValue === 'scroll' ||
        overflowValue === 'overlay');
}
function getScrollableParent(element) {
    var computedStyle = window.getComputedStyle(element);
    if (isOverflowSet(computedStyle.overflow) ||
        isOverflowSet(computedStyle.overflowX) ||
        isOverflowSet(computedStyle.overflowY)) {
        return element;
    }
    if (element.parentNode) {
        return getScrollableParent(element.parentNode);
    }
    return element;
}
function useKeyboardAccessibility(enable, _a) {
    var optionsLength = _a.optionsLength, onSelelected = _a.onSelelected, target = _a.target, _b = _a.resetIndex, resetIndex = _b === void 0 ? true : _b, forceResetIndexDeps = _a.forceResetIndexDeps, onClickEscape = _a.onClickEscape, onClickBackSpace = _a.onClickBackSpace;
    var _c = useState(-1), activeIndex = _c[0], setActiveIndex = _c[1];
    var listChildNodes = useRef();
    var scrollableElement = useRef(null);
    var canMouseSelectActiveIndex = useRef(false);
    var targetNode = useRef(null);
    var selectedIndex = useRef(0);
    /**
     * Logic and handler
     * */
    var handleSetIndex = useCallback(function (index) {
        setActiveIndex(index);
        selectedIndex.current = index;
    }, []);
    var handleSelected = useCallback(function () {
        onSelelected(selectedIndex.current);
        if (resetIndex) {
            handleSetIndex(-1);
            if (scrollableElement.current) {
                scrollableElement.current.scrollTop = 0;
            }
        }
    }, [onSelelected, resetIndex, selectedIndex.current, scrollableElement.current, handleSetIndex]);
    var handleNavigation = useCallback(function (action) {
        setActiveIndex(function (prevIndex) {
            var currentIndex = prevIndex;
            if (targetNode.current && scrollableElement.current) {
                /**
                 * Focus to current item
                 * */
                if (listChildNodes.current) {
                    /**
                     * Set active index
                     * */
                    switch (action) {
                        case '^': {
                            currentIndex--;
                            if (currentIndex < 0) {
                                currentIndex = optionsLength - 1;
                            }
                            break;
                        }
                        case 'v': {
                            currentIndex++;
                            if (currentIndex > optionsLength - 1) {
                                currentIndex = 0;
                            }
                            break;
                        }
                    }
                    /**
                     * Focus to active item
                     * */
                    var child = listChildNodes.current[currentIndex];
                    var childTop = child.offsetTop;
                    var childHeight = child.offsetHeight;
                    var scrollableElementHeight = scrollableElement.current.offsetHeight;
                    var scrollableElementScrollHeight = scrollableElement.current.scrollHeight;
                    var _a = child.getBoundingClientRect(), childBoundTop = _a.top, childBoundBottom = _a.bottom;
                    var _b = scrollableElement.current.getBoundingClientRect(), scrollableElementBoundTop = _b.top, scrollableElementBoundBottom = _b.bottom;
                    var canFocusToActiveElement = false;
                    var childPosition = 0;
                    switch (action) {
                        case '^': {
                            if (childBoundTop < scrollableElementBoundTop) {
                                canFocusToActiveElement = true;
                                childPosition = childTop;
                            }
                            if (childTop + childHeight === scrollableElementScrollHeight) {
                                canFocusToActiveElement = true;
                                childPosition = scrollableElementScrollHeight;
                            }
                            break;
                        }
                        case 'v': {
                            if (childBoundBottom > scrollableElementBoundBottom) {
                                canFocusToActiveElement = true;
                                childPosition = childTop - scrollableElementHeight + childHeight;
                            }
                            if (childTop === 0) {
                                canFocusToActiveElement = true;
                                childPosition = 0;
                            }
                            break;
                        }
                    }
                    if (canFocusToActiveElement) {
                        scrollableElement.current.scrollTo({ top: childPosition, behavior: 'smooth' });
                    }
                }
            }
            selectedIndex.current = currentIndex;
            return currentIndex;
        });
    }, [targetNode.current, optionsLength, listChildNodes]);
    var onKeyDown = useCallback(function (event) {
        // Disable mouse select active index
        canMouseSelectActiveIndex.current = false;
        // Handle keyboard navigation
        switch (event.key) {
            case 'ArrowDown':
                // Do something for "down arrow" key press.
                event.preventDefault();
                handleNavigation('v');
                break;
            case 'ArrowUp':
                event.preventDefault();
                handleNavigation('^');
                break;
            case 'ArrowLeft':
                // Will handle navigation later
                // handleNavigation()
                break;
            case 'ArrowRight':
                // Do something for "right arrow" key press.
                // Will handle navigation later
                // handleNavigation()
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedIndex.current !== -1) {
                    handleSelected();
                }
                break;
            case 'Escape': {
                onClickEscape && onClickEscape();
                break;
            }
            case 'Backspace': {
                onClickBackSpace && onClickBackSpace();
                break;
            }
        }
    }, [handleNavigation, handleSelected, onClickBackSpace, onClickEscape]);
    /**
     * Effects
     * */
    // Set target node and scrollable element
    useEffect(function () {
        if (typeof target === 'string') {
            targetNode.current = document.querySelector(target);
        }
        else {
            targetNode.current = target.current;
        }
        // Only set scrollableElement when have target node
        if (targetNode.current) {
            scrollableElement.current = getScrollableParent(targetNode.current);
        }
    });
    var handleMouseEnter = useCallback(function (index) {
        if (canMouseSelectActiveIndex.current) {
            handleSetIndex(index);
        }
    }, [handleSetIndex]);
    var handleMouseMove = useCallback(function () {
        canMouseSelectActiveIndex.current = true;
    }, []);
    useEffect(function () {
        if (!enable) {
            return;
        }
        if (targetNode.current) {
            targetNode.current.addEventListener('mousemove', handleMouseMove, { passive: true });
            var listChildrenItems = targetNode.current.childNodes;
            listChildrenItems.forEach(function (node, index) {
                node.addEventListener('mouseenter', function () { return handleMouseEnter(index); }, {
                    passive: true
                });
            });
            /**
             * Set list child nodes
             * */
            listChildNodes.current = listChildrenItems;
        }
    }, [handleMouseEnter, targetNode.current, handleMouseMove, enable]);
    // Reset values based on condition
    useEffect(function () {
        handleSetIndex(-1);
        if (scrollableElement.current) {
            scrollableElement.current.scrollTop = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, forceResetIndexDeps);
    // Handle key down
    useEffect(function () {
        if (typeof window === undefined) {
            return;
        }
        if (enable) {
            window.document.addEventListener('keydown', onKeyDown);
        }
        else {
            window.document.removeEventListener('keydown', onKeyDown);
        }
        return function () {
            window.document.removeEventListener('keydown', onKeyDown);
        };
    }, [enable, onKeyDown]);
    return {
        activeIndex: activeIndex
    };
}

export { useKeyboardAccessibility as default };
//# sourceMappingURL=useKeyboardAccessibility.js.map
